import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IconButton, ListItemIcon, ListItemText, Menu, MenuItem } from '@mui/material';
import {
  Add as AddIcon,
  ContentCopy as ContentCopyIcon,
  Delete as DeleteIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
  MoreVert as MoreVertIcon,
} from '@mui/icons-material';
import {
  Box,
  Button,
  CharacterCount,
  Checkbox,
  ConfirmDialog,
  EditableTableCell,
  EditableTableContainer,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@/shared/ui';
import { toast } from '@/shared/feedback/toast';
import type { ProcedureStep, TreatmentCatalogItem } from '../types/treatmentCatalog.types';
import { renumberProcedureSteps } from '../types/treatmentCatalog.types';
import {
  CATALOG_STEP_TITLE_MAX_LENGTH,
  copyProcedureStepsFromCatalog,
} from '../utils/treatmentCatalogItemForm';
import CatalogItemPickerDialog from './CatalogItemPickerDialog';

interface ProcedureStepsEditorProps {
  steps: ProcedureStep[];
  onChange: (steps: ProcedureStep[]) => void;
  error?: string;
  disabled?: boolean;
  kpiCheckEnabled?: boolean;
  /** Catalog id of the service being edited — omitted from the copy-from picker. */
  excludeCatalogId?: string;
  onCopyFrom?: (copied: { procedureSteps: ProcedureStep[] }) => void;
}

const createEmptyStep = (order: number): ProcedureStep => ({
  order,
  title: '',
  description: '',
  durationMinutes: undefined,
  countsTowardKpi: true,
});

const COL = {
  index: 48,
  title: 320,
  duration: 110,
  kpi: 88,
  actions: 48,
} as const;

const TABLE_MIN_WIDTH = COL.index + COL.title + COL.duration + COL.kpi;
const TABLE_MIN_WIDTH_EDITABLE = TABLE_MIN_WIDTH + COL.actions;

const ProcedureStepsEditor: React.FC<ProcedureStepsEditorProps> = ({
  steps,
  onChange,
  error,
  disabled = false,
  kpiCheckEnabled = false,
  excludeCatalogId,
  onCopyFrom,
}) => {
  const { t } = useTranslation(['treatment', 'common']);
  const [menuState, setMenuState] = useState<{ anchorEl: HTMLElement; index: number } | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pendingCopy, setPendingCopy] = useState<{
    item: TreatmentCatalogItem;
    copied: { procedureSteps: ProcedureStep[] };
  } | null>(null);
  const [focusTitleIndex, setFocusTitleIndex] = useState<number | null>(null);
  const titleInputRefs = useRef<Array<HTMLInputElement | null>>([]);

  useEffect(() => {
    if (focusTitleIndex == null) return;
    titleInputRefs.current[focusTitleIndex]?.focus();
    setFocusTitleIndex(null);
  }, [focusTitleIndex, steps.length]);

  const closeMenu = (): void => setMenuState(null);

  const handleAddStep = (): void => {
    onChange([...steps, createEmptyStep(steps.length + 1)]);
    setFocusTitleIndex(steps.length);
  };

  const handleRemoveStep = (index: number): void => {
    const updated = steps.filter((_, i) => i !== index);
    onChange(renumberProcedureSteps(updated));
  };

  const handleMoveStep = (index: number, direction: 'up' | 'down'): void => {
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= steps.length) return;

    const updated = [...steps];
    [updated[index], updated[targetIndex]] = [updated[targetIndex], updated[index]];
    onChange(renumberProcedureSteps(updated));
  };

  const handleStepChange = (
    index: number,
    field: keyof ProcedureStep,
    value: string | number | boolean | undefined
  ): void => {
    const updated = steps.map((step, i) => {
      if (i !== index) return step;
      return { ...step, [field]: value };
    });
    onChange(updated);
  };

  const handleTitleEnter = (index: number): void => {
    if (disabled) return;
    if (!steps[index]?.title.trim()) return;
    const next = steps[index + 1];
    if (next && !next.title.trim()) {
      setFocusTitleIndex(index + 1);
      return;
    }
    const insertAt = index + 1;
    const updated = [...steps];
    updated.splice(insertAt, 0, createEmptyStep(insertAt + 1));
    onChange(renumberProcedureSteps(updated));
    setFocusTitleIndex(insertAt);
  };

  const applyCopy = (
    item: TreatmentCatalogItem,
    copied: { procedureSteps: ProcedureStep[] }
  ): void => {
    if (copied.procedureSteps.length === 0) {
      toast.error(t('catalog.copySteps.noneToCopy'));
      return;
    }
    if (onCopyFrom) {
      onCopyFrom(copied);
    } else {
      onChange(copied.procedureSteps);
    }
    toast.success(
      t('catalog.copySteps.copied', { count: copied.procedureSteps.length, name: item.name })
    );
  };

  const handlePickSource = (item: TreatmentCatalogItem): void => {
    const copied = copyProcedureStepsFromCatalog(item);
    if (copied.procedureSteps.length === 0) {
      toast.error(t('catalog.copySteps.noneToCopy'));
      return;
    }
    if (steps.some((step) => step.title.trim())) {
      setPendingCopy({ item, copied });
      return;
    }
    applyCopy(item, copied);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, gap: 1 }}>
        <Typography variant="h6">{t('catalog.procedureStepsHeading')}</Typography>
        {!disabled && onCopyFrom ? (
          <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
            <Button
              intent="ghost"
              size="small"
              startIcon={<ContentCopyIcon />}
              onClick={() => setPickerOpen(true)}
            >
              {t('catalog.copySteps.action')}
            </Button>
          </Box>
        ) : null}
      </Box>
      <EditableTableContainer
        density="comfortable"
        elevation={0}
        sx={{ boxShadow: 'none', border: 1, borderColor: 'divider' }}
        tableProps={{
          sx: {
            tableLayout: 'fixed',
            width: '100%',
            minWidth: disabled ? TABLE_MIN_WIDTH : TABLE_MIN_WIDTH_EDITABLE,
          },
        }}
      >
        <TableHead>
          <TableRow>
            <TableCell sx={{ width: COL.index }} align="center">#</TableCell>
            <TableCell sx={{ width: COL.title }}>{t('table.step')}</TableCell>
            <TableCell sx={{ width: COL.duration }} align="right">
              {t('table.durationMins')}
            </TableCell>
            <TableCell align="center" sx={{ width: COL.kpi }}>
              {t('table.countsTowardKpi')}
            </TableCell>
            {!disabled ? <TableCell align="center" sx={{ width: COL.actions }} /> : null}
          </TableRow>
        </TableHead>
        <TableBody>
          {steps.length === 0 ? (
            <TableRow>
              <TableCell colSpan={disabled ? 4 : 5} align="center">
                <Typography variant="body2" color="text.secondary">
                  {t('empty.noProcedureStepsAdded')}
                </Typography>
              </TableCell>
            </TableRow>
          ) : (
            steps.map((step, index) => (
              <TableRow key={index}>
                <TableCell align="center" sx={{ color: 'text.secondary' }}>
                  {step.order}
                </TableCell>
                <EditableTableCell>
                  <TextField
                    fullWidth
                    placeholder={t('catalog.stepTitlePlaceholder')}
                    value={step.title}
                    disabled={disabled}
                    inputRef={(el) => {
                      titleInputRefs.current[index] = el;
                    }}
                    onChange={(e) => handleStepChange(index, 'title', e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key !== 'Enter' || e.nativeEvent.isComposing) return;
                      e.preventDefault();
                      handleTitleEnter(index);
                    }}
                    slotProps={{
                      htmlInput: { maxLength: CATALOG_STEP_TITLE_MAX_LENGTH },
                      input: disabled
                        ? undefined
                        : {
                            endAdornment: (
                              <CharacterCount
                                current={step.title.length}
                                max={CATALOG_STEP_TITLE_MAX_LENGTH}
                                placement="adornment"
                              />
                            ),
                          },
                    }}
                  />
                </EditableTableCell>
                <EditableTableCell inputAlign="right">
                  <TextField
                    fullWidth
                    type="number"
                    placeholder={t('catalog.stepMinsPlaceholder')}
                    value={step.durationMinutes ?? ''}
                    disabled={disabled}
                    onChange={(e) => {
                      const value = e.target.value;
                      handleStepChange(
                        index,
                        'durationMinutes',
                        value === '' ? undefined : Number(value)
                      );
                    }}
                    slotProps={{ htmlInput: { min: 0 } }}
                  />
                </EditableTableCell>
                <TableCell align="center" sx={{ py: 0 }}>
                  <Checkbox
                    checked={step.countsTowardKpi !== false}
                    disabled={disabled || !kpiCheckEnabled}
                    onChange={(e) => handleStepChange(index, 'countsTowardKpi', e.target.checked)}
                    inputProps={{ 'aria-label': t('table.countsTowardKpi') }}
                  />
                </TableCell>
                {!disabled ? (
                <TableCell align="center" sx={{ whiteSpace: 'nowrap', py: 0 }}>
                  <IconButton
                    size="small"
                    onClick={(e) => setMenuState({ anchorEl: e.currentTarget, index })}
                    aria-label={t('table.actions')}
                  >
                    <MoreVertIcon fontSize="small" />
                  </IconButton>
                </TableCell>
                ) : null}
              </TableRow>
            ))
          )}
        </TableBody>
      </EditableTableContainer>

      {!disabled ? (
      <Box sx={{ display: 'flex', justifyContent: 'flex-start', mt: 1 }}>
        <Button
          intent="ghost"
          size="tiny"
          startIcon={<AddIcon />}
          onClick={handleAddStep}
        >
          {t('actions.addStep')}
        </Button>
      </Box>
      ) : null}
      <Menu
        anchorEl={menuState?.anchorEl}
        open={Boolean(menuState)}
        onClose={closeMenu}
      >
        <MenuItem
          disabled={menuState?.index === 0}
          onClick={() => {
            if (menuState) handleMoveStep(menuState.index, 'up');
            closeMenu();
          }}
        >
          <ListItemIcon>
            <ArrowUpwardIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t('actions.moveStepUp')}</ListItemText>
        </MenuItem>
        <MenuItem
          disabled={menuState !== null && menuState.index === steps.length - 1}
          onClick={() => {
            if (menuState) handleMoveStep(menuState.index, 'down');
            closeMenu();
          }}
        >
          <ListItemIcon>
            <ArrowDownwardIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t('actions.moveStepDown')}</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (menuState) handleRemoveStep(menuState.index);
            closeMenu();
          }}
          sx={{ color: 'error.main' }}
        >
          <ListItemIcon>
            <DeleteIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText>{t('actions.removeStep')}</ListItemText>
        </MenuItem>
      </Menu>

      {error ? (
        <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }}>
          {error}
        </Typography>
      ) : null}

      <CatalogItemPickerDialog
        presentation="drawer"
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onSelect={handlePickSource}
        title={t('catalog.copySteps.pickerTitle')}
        selectLabel={t('catalog.copySteps.select')}
        emptyMessage={t('catalog.copySteps.empty')}
        includeInactive
        requireProcedureSteps
        excludeItemIds={excludeCatalogId ? [excludeCatalogId] : undefined}
      />
      <ConfirmDialog
        open={Boolean(pendingCopy)}
        title={t('catalog.copySteps.replaceTitle')}
        message={t('catalog.copySteps.replaceMessage', {
          name: pendingCopy?.item.name ?? '',
          count: pendingCopy?.copied.procedureSteps.length ?? 0,
          current: steps.filter((step) => step.title.trim()).length,
        })}
        confirmLabel={t('catalog.copySteps.replaceConfirm')}
        onCancel={() => setPendingCopy(null)}
        onConfirm={() => {
          if (!pendingCopy) return;
          applyCopy(pendingCopy.item, pendingCopy.copied);
          setPendingCopy(null);
        }}
      />
    </Box>
  );
};

export default ProcedureStepsEditor;
